import { useState, useCallback, useEffect, useMemo } from 'react';
import CheckListForm from 'src/components/shared/checklist-form';
import { ChecklistTriggerErrorType, ChecklistTriggerType } from '../types';
import { useAzureProvider } from 'src/azure/context';
import { useAutomationRuleTrigger } from '../use-trigger';
import { getFields } from 'src/azure/services/work-items';
import { getFieldType } from 'src/azure/services/get-field';

type ChecklistTriggerHookState = {
  searchText: string;
  inputMode: 'searching' | 'selected'
  loading: boolean;
  initialFieldOptions: Array<{label: string; value: string}>;
  fieldOptions: Array<{label: string; value: string}>;
}

type Props = {
  validations: ChecklistTriggerErrorType | null;
  data: ChecklistTriggerType;
}

function CheckListTrigger(props: Props) {
	const { applicationContext } = useAzureProvider();
	const useTrigger = useAutomationRuleTrigger();

	const [state, setState] = useState<ChecklistTriggerHookState>({
		searchText: '',
		inputMode: 'selected',
		loading: false,
		initialFieldOptions: [],
		fieldOptions: [],
	});

	const findFieldOptions = useCallback(async () => {
		try {
			const fields = await getFields(applicationContext.projectId);
			if (fields) {
				return fields
					.filter(field =>
						getFieldType(field.type) === 'Html'
					)
					.map(field => ({
						label: field.name,
						value: field.referenceName
					}));
			}
	
			return [];
		} catch (error) {
			console.error('Error finding field options', error);
			throw new Error('Error finding field options');
		}
	}, []);

	const changeState = useCallback((newState: Partial<ChecklistTriggerHookState>) => {
		setState(oldState => ({
			...oldState,
			...newState,
		}));
	}, []);

	const getFieldOptions = useCallback(async () => {
		const fieldOptions = await findFieldOptions();
		changeState({
			loading: true,
			initialFieldOptions: fieldOptions,
			fieldOptions: fieldOptions,
		});
	}, []);

	const resetValidation = useCallback(() => {
		useTrigger.changeValidationError({
			checklistTrigger: {
				field: null,
			}
		});
	}, []);

	const setFieldValue = useCallback((value: string) => {
		resetValidation();
		changeState({
			inputMode: 'selected',
			fieldOptions: state.initialFieldOptions
		});

		useTrigger.changeTriggerData({
			checklistTrigger: {
				field: value,
			}
		});
	}, [state]);

	const onChangeSearchText = useCallback((value: string) => {
		changeState({
			inputMode: 'searching',
			searchText: value,
			fieldOptions: state.initialFieldOptions.filter(option => option.label.toLowerCase().includes(value.toLowerCase())),
		});
	}, []);

	const renderValue = useMemo(() => {
		if (state.inputMode === 'searching') {
			return state.searchText;
		}

		return state.fieldOptions.find(option => option.value === useTrigger.triggerData?.checklistTrigger?.field)?.label || '';
	}, [
		state.inputMode,
		state.searchText,
		useTrigger.triggerData?.checklistTrigger?.field,
		state.fieldOptions,
	]);

	useEffect(() => {
		const asyncHandler = async () => {
			resetValidation();
			changeState({ loading: true });
			await getFieldOptions();
			changeState({ loading: false });
		};

		asyncHandler();
	}, []);

	return (
		<CheckListForm
			loading={state.loading}
			validation={props.validations?.field || ''}
			selectedOptions={[useTrigger.triggerData?.checklistTrigger?.field || '']}
			value={renderValue}
			changeSelectOptions={setFieldValue}
			changeTextValue={onChangeSearchText}
			options={state.fieldOptions}
		/>
	);
}

export default CheckListTrigger;

